import { DatePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { formatDayCount } from '../retention-day-count'

const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000

// A label centred on its own mark runs out of the box near either end of the scale, so past these
// points it is aligned to start or to end at the mark instead.
const ALIGN_TO_END_PERCENTAGE = 75
const ALIGN_TO_START_PERCENTAGE = 25

/** What the timeline draws. Every date is in the reader's own time zone, as the dialog's are. */
export type RetentionTimelineView = {
  meetingDate: Date
  /** Where the soft deletion stood when the dialog opened, which the marker is dragged away from. */
  currentSoftDeletionDate: Date
  softDeletionDate: Date
  deletionDate: Date
  originalSoftDeletionDate: Date
  originalDeletionDate: Date
  /** Null when the organization caps nothing, in which case no maximum is drawn. */
  maximumSoftDeletionDate: Date | null
  maximumDeletionDate: Date | null
  /** The right-hand edge of the scale, which the dialog picks. */
  windowEndDate: Date
  recoveryWindowInDays: number
  /** How far the deletion has been pushed from where it stood before any extension. */
  extendedFromOriginalInDays: number
  /** The marker's own position, in days later than the date it sits on now. */
  extensionInDays: number
  maximumExtensionInDays: number
}

@Component({
  selector: 'app-retention-timeline',
  standalone: true,
  imports: [DatePipe, TranslatePipe, TranslateDirective],
  templateUrl: './retention-timeline.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './retention-timeline.component.scss',
})
export class RetentionTimelineComponent {
  private translateService = inject(TranslateService)

  @Input({ required: true }) view!: RetentionTimelineView

  /** How many days later than its current date the soft deletion has been moved to. */
  @Output() extensionInDaysChange = new EventEmitter<number>()

  @ViewChild('track', { static: true }) private track!: ElementRef<HTMLElement>

  /** The day alone: the times are in the fields below, where they can be read and edited. */
  readonly dateFormat = 'd MMM y'

  private isDragging = false

  get extendedByLabel(): string {
    const days = this.view.extendedFromOriginalInDays
    const label = formatDayCount(this.translateService, days)
    return days > 0 ? `+${label}` : label
  }

  get recoveryWindowLabel(): string {
    return formatDayCount(this.translateService, this.view.recoveryWindowInDays)
  }

  /** Where a date sits on the scale, clamped so a date past the edge still draws inside the box. */
  percentageFor(date: Date): number {
    const span = this.view.windowEndDate.getTime() - this.view.meetingDate.getTime()
    if (span <= 0) {
      return 0
    }
    const fraction = (date.getTime() - this.view.meetingDate.getTime()) / span
    return Math.min(100, Math.max(0, fraction * 100))
  }

  /** Whether a label at this date would run off the right of the box if it were centred on it. */
  isNearEnd(date: Date): boolean {
    return this.percentageFor(date) > ALIGN_TO_END_PERCENTAGE
  }

  /** The same at the left, where the meeting end of the scale is. */
  isNearStart(date: Date): boolean {
    return this.percentageFor(date) < ALIGN_TO_START_PERCENTAGE
  }

  /** The stretch between the two markers, which is the window the meeting can still be recovered in. */
  get recoverableWidthPercentage(): number {
    return Math.max(0, this.percentageFor(this.view.deletionDate) - this.percentageFor(this.view.softDeletionDate))
  }

  startDragging(event: PointerEvent) {
    // Keeps the moves coming while the pointer runs past the edge of the marker, and stops the
    // browser reading the drag as a text selection.
    event.preventDefault()
    ;(event.target as HTMLElement).setPointerCapture(event.pointerId)
    this.isDragging = true
  }

  continueDragging(event: PointerEvent) {
    if (this.isDragging) {
      this.emitExtension(this.extensionAt(event.clientX))
    }
  }

  stopDragging() {
    this.isDragging = false
  }

  handleKeyDown(event: KeyboardEvent) {
    const steps: Record<string, number> = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1, PageUp: 7, PageDown: -7 }
    if (Object.hasOwn(steps, event.key)) {
      event.preventDefault()
      this.emitExtension(this.view.extensionInDays + steps[event.key])
    } else if (event.key === 'Home') {
      event.preventDefault()
      this.emitExtension(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      this.emitExtension(this.view.maximumExtensionInDays)
    }
  }

  private extensionAt(clientX: number): number {
    const { left, width } = this.track.nativeElement.getBoundingClientRect()
    if (width === 0) {
      return this.view.extensionInDays
    }
    const fraction = Math.min(1, Math.max(0, (clientX - left) / width))
    const span = this.view.windowEndDate.getTime() - this.view.meetingDate.getTime()
    const date = this.view.meetingDate.getTime() + fraction * span
    return Math.round((date - this.view.currentSoftDeletionDate.getTime()) / MILLISECONDS_IN_DAY)
  }

  private emitExtension(days: number) {
    const clamped = Math.min(this.view.maximumExtensionInDays, Math.max(0, days))
    if (clamped !== this.view.extensionInDays) {
      this.extensionInDaysChange.emit(clamped)
    }
  }
}
